import React, { useState, useEffect, useCallback } from 'react';
import { Accessibility, X, Type, Sun, Moon, MousePointer, RotateCcw, Minus, Plus, Underline, Pause } from 'lucide-react';

const STORAGE_KEY = 'renew_a11y_settings';

const defaultSettings = {
  fontSize: 0,
  highContrast: false,
  highlightLinks: false,
  reducedMotion: false,
  largerCursor: false,
  textSpacing: false,
};

const AccessibilityWidget = () => {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(defaultSettings);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSettings(parsed);
        applySettings(parsed);
      } catch {}
    }
  }, []);

  const applySettings = useCallback((s) => {
    const root = document.documentElement;

    // Font size
    root.style.fontSize = s.fontSize === 0 ? '' : `${100 + s.fontSize * 15}%`;

    // High contrast
    root.classList.toggle('a11y-high-contrast', s.highContrast);

    // Highlight links
    root.classList.toggle('a11y-highlight-links', s.highlightLinks);

    // Reduced motion
    root.classList.toggle('a11y-reduced-motion', s.reducedMotion);

    // Larger cursor
    root.classList.toggle('a11y-larger-cursor', s.largerCursor);

    // Text spacing
    root.classList.toggle('a11y-text-spacing', s.textSpacing);
  }, []);

  const update = (key, value) => {
    const next = { ...settings, [key]: value };
    setSettings(next);
    applySettings(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  const reset = () => {
    setSettings(defaultSettings);
    applySettings(defaultSettings);
    localStorage.removeItem(STORAGE_KEY);
  };

  const adjustFont = (dir) => {
    const next = Math.max(-2, Math.min(4, settings.fontSize + dir));
    update('fontSize', next);
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 left-6 z-[100] w-12 h-12 rounded-full bg-[#00F0FF] text-black flex items-center justify-center shadow-lg hover:scale-110 transition-transform focus:outline-none focus:ring-2 focus:ring-[#00F0FF] focus:ring-offset-2 focus:ring-offset-[#050505]"
        aria-label="Open accessibility settings"
        data-testid="a11y-toggle"
      >
        <Accessibility className="w-6 h-6" />
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-20 left-6 z-[100] w-80 bg-[#0C0E14] border border-[#1E2532] rounded-2xl shadow-2xl overflow-hidden"
          role="dialog"
          aria-label="Accessibility settings"
          data-testid="a11y-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#1E2532]">
            <div className="flex items-center gap-2">
              <Accessibility className="w-5 h-5 text-[#00F0FF]" />
              <span className="text-white font-semibold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>Accessibility</span>
            </div>
            <button onClick={() => setOpen(false)} className="text-[#A1A7B5] hover:text-white transition-colors" aria-label="Close accessibility panel" data-testid="a11y-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
            {/* Font Size */}
            <div className="flex items-center justify-between p-3 bg-[#111621] rounded-lg">
              <div className="flex items-center gap-2">
                <Type className="w-4 h-4 text-[#00F0FF]" />
                <span className="text-white text-sm">Font Size</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => adjustFont(-1)}
                  disabled={settings.fontSize <= -2}
                  className="w-7 h-7 rounded bg-[#1E2532] text-white flex items-center justify-center hover:bg-[#2a3040] disabled:opacity-30 transition-colors"
                  aria-label="Decrease font size"
                  data-testid="a11y-font-decrease"
                >
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <span className="text-white text-xs w-8 text-center font-mono">{settings.fontSize > 0 ? '+' : ''}{settings.fontSize}</span>
                <button
                  onClick={() => adjustFont(1)}
                  disabled={settings.fontSize >= 4}
                  className="w-7 h-7 rounded bg-[#1E2532] text-white flex items-center justify-center hover:bg-[#2a3040] disabled:opacity-30 transition-colors"
                  aria-label="Increase font size"
                  data-testid="a11y-font-increase"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* High Contrast */}
            <button
              onClick={() => update('highContrast', !settings.highContrast)}
              className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${settings.highContrast ? 'bg-[#00F0FF]/15 border border-[#00F0FF]' : 'bg-[#111621] border border-transparent'}`}
              aria-pressed={settings.highContrast}
              data-testid="a11y-contrast"
            >
              <div className="flex items-center gap-2">
                {settings.highContrast ? <Moon className="w-4 h-4 text-[#00F0FF]" /> : <Sun className="w-4 h-4 text-[#A1A7B5]" />}
                <span className="text-white text-sm">High Contrast</span>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${settings.highContrast ? 'bg-[#00F0FF] text-black' : 'bg-[#1E2532] text-[#A1A7B5]'}`}>
                {settings.highContrast ? 'ON' : 'OFF'}
              </span>
            </button>

            {/* Highlight Links */}
            <button
              onClick={() => update('highlightLinks', !settings.highlightLinks)}
              className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${settings.highlightLinks ? 'bg-[#00F0FF]/15 border border-[#00F0FF]' : 'bg-[#111621] border border-transparent'}`}
              aria-pressed={settings.highlightLinks}
              data-testid="a11y-links"
            >
              <div className="flex items-center gap-2">
                <Underline className="w-4 h-4 text-[#A1A7B5]" />
                <span className="text-white text-sm">Highlight Links</span>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${settings.highlightLinks ? 'bg-[#00F0FF] text-black' : 'bg-[#1E2532] text-[#A1A7B5]'}`}>
                {settings.highlightLinks ? 'ON' : 'OFF'}
              </span>
            </button>

            {/* Text Spacing */}
            <button
              onClick={() => update('textSpacing', !settings.textSpacing)}
              className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${settings.textSpacing ? 'bg-[#00F0FF]/15 border border-[#00F0FF]' : 'bg-[#111621] border border-transparent'}`}
              aria-pressed={settings.textSpacing}
              data-testid="a11y-spacing"
            >
              <div className="flex items-center gap-2">
                <Type className="w-4 h-4 text-[#A1A7B5]" />
                <span className="text-white text-sm">Text Spacing</span>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${settings.textSpacing ? 'bg-[#00F0FF] text-black' : 'bg-[#1E2532] text-[#A1A7B5]'}`}>
                {settings.textSpacing ? 'ON' : 'OFF'}
              </span>
            </button>

            {/* Reduced Motion */}
            <button
              onClick={() => update('reducedMotion', !settings.reducedMotion)}
              className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${settings.reducedMotion ? 'bg-[#00F0FF]/15 border border-[#00F0FF]' : 'bg-[#111621] border border-transparent'}`}
              aria-pressed={settings.reducedMotion}
              data-testid="a11y-motion"
            >
              <div className="flex items-center gap-2">
                <Pause className="w-4 h-4 text-[#A1A7B5]" />
                <span className="text-white text-sm">Reduce Motion</span>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${settings.reducedMotion ? 'bg-[#00F0FF] text-black' : 'bg-[#1E2532] text-[#A1A7B5]'}`}>
                {settings.reducedMotion ? 'ON' : 'OFF'}
              </span>
            </button>

            {/* Larger Cursor */}
            <button
              onClick={() => update('largerCursor', !settings.largerCursor)}
              className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${settings.largerCursor ? 'bg-[#00F0FF]/15 border border-[#00F0FF]' : 'bg-[#111621] border border-transparent'}`}
              aria-pressed={settings.largerCursor}
              data-testid="a11y-cursor"
            >
              <div className="flex items-center gap-2">
                <MousePointer className="w-4 h-4 text-[#A1A7B5]" />
                <span className="text-white text-sm">Larger Cursor</span>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${settings.largerCursor ? 'bg-[#00F0FF] text-black' : 'bg-[#1E2532] text-[#A1A7B5]'}`}>
                {settings.largerCursor ? 'ON' : 'OFF'}
              </span>
            </button>

            {/* Reset */}
            <button
              onClick={reset}
              className="w-full flex items-center justify-center gap-2 p-3 bg-[#1E2532] hover:bg-[#2a3040] rounded-lg transition-colors text-[#A1A7B5] hover:text-white"
              data-testid="a11y-reset"
            >
              <RotateCcw className="w-4 h-4" />
              <span className="text-sm">Reset All</span>
            </button>
          </div>

          <div className="px-5 py-3 border-t border-[#1E2532]">
            <p className="text-[#A1A7B5] text-[10px] text-center">WCAG 2.1 AA Compliant</p>
          </div>
        </div>
      )}
    </>
  );
};

export default AccessibilityWidget;
